import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth';
import { useUserStore } from '@/store/userStore';
import { cn } from '@/utils/cn';
import { isPaidPlan } from '@/utils/planHelpers';
import { ChevronDown, User, Crown, RefreshCw } from 'lucide-react';

export function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const { user, refreshUser, isLoading } = useUserStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const credits = user?.remainingCredit ?? 0;
  const isPro = isPaidPlan(user?.userPlan);
  const displayName = user?.displayName || user?.name || firebaseUser?.displayName || t('account.guestUser');
  const avatarUrl = user?.profilePictureUrl || user?.avatarUrl || firebaseUser?.photoURL;

  return (
    <header className="h-16 bg-surface/80 backdrop-blur-md border-b border-glass-border flex items-center justify-between px-6 sticky top-0 z-30">
      {/* Left side - can be used for breadcrumbs or search */}
      <div className="flex-1" />

      {/* Right side - Credits and User */}
      <div className="flex items-center gap-4">
        {/* Credits Badge - matching mobile style */}
        <button
          onClick={() => navigate('/subscription')}
          className="flex items-center gap-2 px-4 py-2 rounded-3xl bg-surface border border-primary/30 hover:border-primary/50 transition-all group"
        >
          <span className="text-sm text-text-secondary">{t('account.credits')}</span>
          <div className="px-3 py-1 rounded-xl bg-gradient-to-r from-primary to-accent shadow-[0_0_12px_rgba(139,92,246,0.4)] group-hover:shadow-[0_0_16px_rgba(139,92,246,0.5)] transition-shadow">
            <span className="text-sm font-bold text-white tracking-wide">{isPro ? credits : t('subscription.free')}</span>
          </div>
        </button>

        {/* Refresh Button */}
        <button
          onClick={() => refreshUser()}
          disabled={isLoading}
          className="p-2 rounded-xl hover:bg-surface-light transition-colors"
          title={t('common.refresh')}
        >
          <RefreshCw className={cn('w-5 h-5 text-text-secondary', isLoading && 'animate-spin')} />
        </button>

        {/* User Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-surface-light transition-colors"
          >
            {/* Avatar */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
            )}
            
            {/* Name and Plan */}
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-text-primary">{displayName}</p>
              <p className="text-xs text-text-tertiary flex items-center gap-1">
                {isPro && <Crown className="w-3 h-3 text-amber-500" />}
                {user?.userPlan === 'max' ? t('subscription.max') : isPro ? t('subscription.pro') : t('subscription.free')} {t('generator.plan')}
              </p>
            </div>
            
            <ChevronDown className={cn(
              'w-4 h-4 text-text-tertiary transition-transform',
              isDropdownOpen && 'rotate-180'
            )} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 py-2 bg-surface border border-glass-border rounded-xl shadow-xl animate-fade-in">
              <div className="px-4 py-3 border-b border-glass-border">
                <p className="text-sm font-medium text-text-primary truncate">{displayName}</p>
                <p className="text-xs text-text-tertiary truncate">{firebaseUser?.email}</p>
              </div>
              
              {!isPro && (
                <div className="py-2">
                  <button
                    onClick={() => {
                      navigate('/subscription');
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
                  >
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-text-primary">{t('subscription.upgradeToAccess')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
